// pages/LeaveManagement.jsx
import { useState, useEffect } from 'react';
import leaveController from '../../controllers/leaveController';
import Leave from '../../models/Leave';

import { 
  Plus, Search, Download, Eye, X, AlertCircle, ChevronUp, ChevronDown, 
  Calendar, CheckCircle, XCircle, Clock, CalendarDays, FileText, XOctagon 
} from 'lucide-react';

const LeaveManagementPage = () => {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [formData, setFormData] = useState({
    leaveType: 'Casual',
    startDate: '',
    endDate: '',
    reason: '',
    halfDay: false,
    contactPhone: '',
    emergencyContact: '',
    emergencyPhone: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('appliedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [leaveBalance, setLeaveBalance] = useState({
    casual: 0,
    sick: 0,
    paid: 0,
    total: 0
  });

  // Helper function to extract employee ID from object or string
  const getEmployeeId = (employeeId) => {
    if (!employeeId) return 'N/A';
    return typeof employeeId === 'object' ? employeeId.employeeId : employeeId;
  };

  useEffect(() => {
    fetchLeaves();
    fetchLeaveBalance();
  }, []);

  useEffect(() => {
    let result = [...leaves];
    
    if (searchQuery) {
      result = result.filter(leave => {
        const empId = getEmployeeId(leave.employeeId).toLowerCase();
        const reason = leave.reason?.toLowerCase() || '';
        const leaveType = leave.leaveType?.toLowerCase() || '';
        const status = leave.status?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();
        
        return empId.includes(query) || 
               reason.includes(query) || 
               leaveType.includes(query) || 
               status.includes(query);
      });
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(leave => leave.status === filterStatus);
    }
    
    if (filterType !== 'All') {
      result = result.filter(leave => leave.leaveType === filterType);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      
      // Handle nested objects for sorting
      if (sortField === 'employeeId') {
        aVal = getEmployeeId(aVal);
        bVal = getEmployeeId(bVal);
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredLeaves(result);
    setCurrentPage(1);
  }, [leaves, searchQuery, filterStatus, filterType, sortField, sortDirection]);

  const fetchLeaves = async () => {
    setLoading(true);
    const result = await leaveController.getAll({ page: 1, limit: 100 });
    if (result.success) {
      setLeaves(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchLeaveBalance = async () => {
    const result = await leaveController.getBalance();
    if (result.success) {
      setLeaveBalance(result.data);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // If half day is checked, set end date same as start date
      if (name === 'halfDay' && checked && updated.startDate) {
        updated.endDate = updated.startDate;
      }
      
      return updated;
    });
    setFormErrors([]);
  };

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    
    if (formData.halfDay) return 0.5;
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end - start);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return days;
  };

  const handleApply = () => {
    setFormData({
      leaveType: 'Casual',
      startDate: '',
      endDate: '',
      reason: '',
      halfDay: false,
      contactPhone: '',
      emergencyContact: '',
      emergencyPhone: ''
    });
    setFormErrors([]);
    setShowApplyModal(true);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    // Basic validation
    if (!formData.startDate) {
      setFormErrors(['Start date is required']);
      setFormSubmitting(false);
      return;
    }

    if (!formData.endDate && !formData.halfDay) {
      setFormErrors(['End date is required']);
      setFormSubmitting(false);
      return;
    }

    if (!formData.reason || formData.reason.trim().length < 10) {
      setFormErrors(['Reason must be at least 10 characters']);
      setFormSubmitting(false);
      return;
    }

    // Validate dates
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      
      if (end < start) {
        setFormErrors(['End date cannot be before start date']);
        setFormSubmitting(false);
        return;
      }
    }

    // Prepare data matching backend schema
    const leaveData = {
      leaveType: formData.leaveType,
      startDate: formData.startDate,
      endDate: formData.halfDay ? formData.startDate : formData.endDate,
      numberOfDays: calculateDays(), // Backend uses 'numberOfDays' not 'days'
      reason: formData.reason.trim(),
      halfDay: formData.halfDay
    };

    // Add optional fields only if they have values
    if (formData.contactPhone) {
      leaveData.contactPhone = formData.contactPhone;
    }
    if (formData.emergencyContact) {
      leaveData.emergencyContact = formData.emergencyContact;
    }
    if (formData.emergencyPhone) {
      leaveData.emergencyPhone = formData.emergencyPhone;
    }

    const result = await leaveController.create(leaveData);
    
    if (result.success) {
      setShowApplyModal(false);
      fetchLeaves();
      fetchLeaveBalance();
      alert('✅ Leave application submitted successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleView = (leave) => {
    setSelectedLeave(leave);
    setShowViewModal(true);
  };

  const handleCancel = async (leave) => {
    const isConfirmed = window.confirm('Are you sure you want to cancel this leave application?');
    if (!isConfirmed) return;

    const result = await leaveController.cancel(leave.id);
    
    if (result.success) {
      fetchLeaves();
      fetchLeaveBalance();
      setShowViewModal(false);
      alert('✅ Leave cancelled successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterType !== 'All') filters.leaveType = filterType;
    
    const result = await leaveController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leaves-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Leaves exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeaves = filteredLeaves.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLeaves.length / itemsPerPage);

  const stats = {
    total: leaves.length,
    pending: leaves.filter(l => l.status === 'Pending').length,
    approved: leaves.filter(l => l.status === 'Approved').length,
    rejected: leaves.filter(l => l.status === 'Rejected').length
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { 
      fontSize: '28px', 
      fontWeight: 'bold', 
      color: '#1e293b', 
      margin: 0 
    },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer', 
      transition: 'all 0.2s' 
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    },
    statLabel: { 
      fontSize: '14px', 
      color: '#64748b', 
      marginBottom: '8px' 
    },
    statValue: { 
      fontSize: '28px', 
      fontWeight: 'bold', 
      color: '#1e293b' 
    },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap' 
    },
    searchBox: { 
      flex: 1, 
      minWidth: '250px', 
      position: 'relative' 
    },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { 
      width: '100%', 
      borderCollapse: 'collapse' 
    },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer', 
      transition: 'background-color 0.2s' 
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '800px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { 
      fontSize: '20px', 
      fontWeight: '600', 
      color: '#1e293b', 
      margin: 0 
    },
    modalBody: { 
      padding: '24px' 
    },
    formGroup: { 
      marginBottom: '20px' 
    },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      transition: 'border-color 0.2s' 
    },
    textarea: {
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      resize: 'vertical',
      minHeight: '100px',
      fontFamily: 'inherit'
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    balanceCard: {
      backgroundColor: '#fff',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    daysInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      cursor: 'pointer',
      marginTop: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Leave Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleApply}>
            <Plus size={18} /> Apply Leave
          </button>
        </div>
      </div>

      {/* Leave Balance Cards */}
      <div style={styles.statsBar}>
        <div style={styles.balanceCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Balance</div>
            <Calendar size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{leaveBalance.total} days</div>
        </div>
        
        <div style={styles.balanceCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Casual Leave</div>
            <CalendarDays size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{leaveBalance.casual} days</div>
        </div>
        
        <div style={styles.balanceCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Sick Leave</div>
            <FileText size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{leaveBalance.sick} days</div>
        </div>
        
        <div style={styles.balanceCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Paid Leave</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{leaveBalance.paid} days</div>
        </div>
      </div>

      {/* Leave Stats */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Applications</div>
            <FileText size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Pending</div>
            <Clock size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.pending}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Approved</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.approved}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Rejected</div>
            <XCircle size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.rejected}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search leaves..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        
        <select 
          style={styles.select} 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="Casual">Casual</option>
          <option value="Sick">Sick</option>
          <option value="Paid">Paid</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Maternity">Maternity</option>
          <option value="Paternity">Paternity</option>
        </select>
      </div>

      {/* Leaves Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading leaves...
          </div>
        ) : currentLeaves.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No leaves found. Click "Apply Leave" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('leaveType')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Leave Type
                      {sortField === 'leaveType' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('startDate')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Start Date
                      {sortField === 'startDate' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('endDate')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      End Date
                      {sortField === 'endDate' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th}>Days</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th} onClick={() => handleSort('status')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentLeaves.map((leave) => (
                  <tr key={leave.id} style={{ cursor: 'pointer' }}>
                    <td style={styles.td} onClick={() => handleView(leave)}>
                      <span 
                        style={{ 
                          ...styles.badge, 
                          backgroundColor: `${leave.getLeaveTypeColor()}20`, 
                          color: leave.getLeaveTypeColor() 
                        }}
                      >
                        {leave.leaveType}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(leave.startDate)}</td>
                    <td style={styles.td}>{formatDate(leave.endDate)}</td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600', color: '#3b82f6' }}>
                        {leave.days} {leave.days === 1 ? 'day' : 'days'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {leave.reason}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span 
                        style={{ 
                          ...styles.badge, 
                          backgroundColor: `${leave.getStatusColor()}20`, 
                          color: leave.getStatusColor() 
                        }}
                      >
                        {leave.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          style={styles.actionButton} 
                          onClick={(e) => { e.stopPropagation(); handleView(leave); }} 
                          title="View"
                        >
                          <Eye size={18} color="#64748b" />
                        </button>
                        {leave.status === 'Pending' && (
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleCancel(leave); }} 
                            title="Cancel"
                          >
                            <XOctagon size={18} color="#ef4444" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLeaves.length)} of {filteredLeaves.length} leaves
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages || 1}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div style={styles.modal} onClick={() => setShowApplyModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Apply for Leave</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowApplyModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleApplySubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Leave Type *</label>
                    <select 
                      name="leaveType" 
                      style={styles.input} 
                      value={formData.leaveType} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="Casual">Casual Leave</option>
                      <option value="Sick">Sick Leave</option>
                      <option value="Paid">Paid Leave</option>
                      <option value="Unpaid">Unpaid Leave</option>
                      <option value="Maternity">Maternity Leave</option>
                      <option value="Paternity">Paternity Leave</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.checkboxLabel}>
                      <input 
                        type="checkbox" 
                        name="halfDay" 
                        checked={formData.halfDay} 
                        onChange={handleInputChange}
                        style={{ marginRight: '8px' }}
                      />
                      Half Day Leave
                    </label>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Start Date *</label>
                    <input 
                      type="date" 
                      name="startDate" 
                      style={styles.input} 
                      value={formData.startDate} 
                      onChange={handleInputChange} 
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>End Date *</label>
                    <input 
                      type="date" 
                      name="endDate" 
                      style={styles.input} 
                      value={formData.endDate} 
                      onChange={handleInputChange} 
                      required
                      min={formData.startDate || new Date().toISOString().split('T')[0]}
                      disabled={formData.halfDay}
                    />
                  </div>
                </div>

                {(formData.startDate && (formData.endDate || formData.halfDay)) && (
                  <div style={styles.daysInfo}>
                    <Calendar size={20} color="#10b981" />
                    <span style={{ fontSize: '14px', color: '#047857' }}>
                      Total Days: <strong>{calculateDays()}</strong>
                    </span>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Reason *</label>
                  <textarea 
                    name="reason" 
                    style={styles.textarea} 
                    value={formData.reason} 
                    onChange={handleInputChange} 
                    placeholder="Please provide a detailed reason for leave..."
                    required
                    minLength={10}
                  ></textarea>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Contact Number During Leave</label>
                    <input 
                      type="tel" 
                      name="contactPhone" 
                      style={styles.input} 
                      value={formData.contactPhone} 
                      onChange={handleInputChange} 
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Emergency Contact Name</label>
                    <input 
                      type="text" 
                      name="emergencyContact" 
                      style={styles.input} 
                      value={formData.emergencyContact} 
                      onChange={handleInputChange} 
                      placeholder="Enter contact name"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Emergency Contact Phone</label>
                  <input 
                    type="tel" 
                    name="emergencyPhone" 
                    style={styles.input} 
                    value={formData.emergencyPhone} 
                    onChange={handleInputChange} 
                    placeholder="Enter emergency phone number"
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setShowApplyModal(false)} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Leave Modal */}
      {showViewModal && selectedLeave && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Leave Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getEmployeeId(selectedLeave.employeeId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Leave Type</div>
                  <span 
                    style={{ 
                      ...styles.badge, 
                      backgroundColor: `${selectedLeave.getLeaveTypeColor()}20`, 
                      color: selectedLeave.getLeaveTypeColor() 
                    }}
                  >
                    {selectedLeave.leaveType}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span 
                    style={{ 
                      ...styles.badge, 
                      backgroundColor: `${selectedLeave.getStatusColor()}20`, 
                      color: selectedLeave.getStatusColor() 
                    }}
                  >
                    {selectedLeave.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Duration</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#3b82f6' }}>
                    {selectedLeave.days} {selectedLeave.days === 1 ? 'day' : 'days'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Start Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {formatDate(selectedLeave.startDate)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>End Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {formatDate(selectedLeave.endDate)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Applied Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {formatDate(selectedLeave.appliedDate)}
                  </div>
                </div>

                {selectedLeave.approvedDate && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Approved Date</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#10b981' }}>
                      {formatDate(selectedLeave.approvedDate)}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '24px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Reason</div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#1e293b', 
                  padding: '12px', 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '8px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedLeave.reason}
                </div>
              </div>

              {selectedLeave.rejectionReason && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '8px' }}>Rejection Reason</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#dc2626', 
                    padding: '12px', 
                    backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                    borderRadius: '8px',
                    borderLeft: '3px solid #ef4444',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedLeave.rejectionReason}
                  </div>
                </div>
              )}

              {selectedLeave.approvedBy && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Approved By</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getEmployeeId(selectedLeave.approvedBy)}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {selectedLeave.status === 'Pending' && (
                <button 
                  style={{ ...styles.button, backgroundColor: '#ef4444' }} 
                  onClick={() => { 
                    handleCancel(selectedLeave); 
                  }}
                >
                  <XOctagon size={16} /> Cancel Leave
                </button>
              )}
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagementPage;